import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import FoodSelector from './FoodSelector';
import MacroTargets from './MacroTargets';
import MealResults from './MealResults';
import CustomFoodForm from './CustomFoodForm';
import PaymentModal from './PaymentModal';
import FeedbackModal from './FeedbackModal';
import GetStartedGuide from './GetStartedGuide';
import AnalyticsModal from './AnalyticsModal';
import UserProfileForm from './UserProfileForm';
import { foodDatabase, getConversionFactor } from '../data/foodDatabase';
import { useAuth } from './Auth/AuthContext';

const { FiTarget, FiCalculator, FiCheckCircle, FiPlusCircle, FiStar, FiZap, FiMessageCircle, FiHelpCircle, FiBarChart, FiMenu, FiX, FiShare2, FiUser, FiSliders } = FiIcons;

const MealCalculator = () => {
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [targets, setTargets] = useState({ calories: '', protein: '', carbs: '', fat: '' });
  const [thresholds, setThresholds] = useState({ calories: 10, protein: 0, carbs: 0, fat: 0, advancedMode: false });
  const [mealPlan, setMealPlan] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showCustomFoodForm, setShowCustomFoodForm] = useState(false);
  const [customFoods, setCustomFoods] = useState([]);
  const [hasShownPaymentPrompt, setHasShownPaymentPrompt] = useState(false);
  
  // Modal states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showGetStartedGuide, setShowGetStartedGuide] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // User profile form
  const [showUserProfileForm, setShowUserProfileForm] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [setupComplete, setSetupComplete] = useState(false);

  // Auth context
  const { 
    user, 
    loading, 
    isPremium, 
    needsPayment: checkNeedsPayment, 
    recordFreeTrialUse, 
    getRemainingFreeTrials,
    setShowLoginModal,
    setShowPaymentModal
  } = useAuth();

  // Check for initial profile setup
  useEffect(() => {
    const profileData = localStorage.getItem('user_profile');
    if (profileData) {
      setUserProfile(JSON.parse(profileData));
      setSetupComplete(true);
      
      // If user has a profile, auto-populate the targets
      const profile = JSON.parse(profileData);
      if (profile && profile.calculatedTargets) {
        setTargets(profile.calculatedTargets);
        
        // Set thresholds based on advanced mode - enable advanced mode by default
        if (profile.calculatedTargets.protein && profile.calculatedTargets.carbs && profile.calculatedTargets.fat) {
          setThresholds({
            calories: 10,
            protein: 15,
            carbs: 15,
            fat: 15,
            advancedMode: true
          });
        }
      }
    } else {
      // Show profile form for new users
      setShowUserProfileForm(true);
    }
    
    // Show get started guide for new users
    const hasSeenGuide = localStorage.getItem('meal_calculator_seen_guide');
    if (!hasSeenGuide) {
      setShowGetStartedGuide(true);
      localStorage.setItem('meal_calculator_seen_guide', 'true');
    }
  }, []);

  // Reset payment prompt flag when user changes
  useEffect(() => {
    setHasShownPaymentPrompt(false);
  }, [user]);

  const calculateMeal = async () => {
    if (selectedFoods.length === 0 || !targets.calories) return;
    
    // Check if user needs to pay
    if (checkNeedsPayment()) {
      // If not logged in, show login modal first
      if (!user) {
        setShowLoginModal(true);
        return;
      } else {
        // User is logged in but needs to pay
        setShowPaymentModal(true);
        setHasShownPaymentPrompt(true);
        return;
      }
    }

    setIsCalculating(true);
    
    // Record calculation attempt
    try {
      // Simulate calculation delay for better UX
      setTimeout(async () => {
        const result = optimizeMeal(selectedFoods, targets, thresholds);
        setMealPlan(result);
        setIsCalculating(false);
        setCurrentStep(3);
        
        // Check if calculation was successful and record it
        if (result && result.foods && result.foods.length > 0 && 
            isCalculationSuccessful(result, targets, thresholds)) {
          // Record successful calculation
          await recordFreeTrialUse();
          
          // Check if this calculation pushed them over the limit
          const remainingTrials = await getRemainingFreeTrials();
          if (remainingTrials === 0 && !isPremium) {
            // Show payment modal if they've used all trials
            setTimeout(() => {
              setShowPaymentModal(true);
              setHasShownPaymentPrompt(true);
            }, 500);
          }
        }
      }, 1500);
    } catch (error) {
      console.error('Error during meal calculation:', error);
      setIsCalculating(false);
    }
  };

  // Check if calculation was successful
  const isCalculationSuccessful = (mealPlan, targets, thresholds) => {
    if (!mealPlan || !targets) return false;
    
    const { totals } = mealPlan;
    const targetCals = parseFloat(targets.calories);
    
    // Check calorie threshold
    const calorieError = Math.abs(totals.calories - targetCals) / targetCals;
    const withinCalorieThreshold = calorieError <= (thresholds.calories / 100);
    
    if (!thresholds.advancedMode) {
      return withinCalorieThreshold;
    }
    
    // Check advanced mode thresholds
    const targetProtein = parseFloat(targets.protein) || 0;
    const targetCarbs = parseFloat(targets.carbs) || 0;
    const targetFat = parseFloat(targets.fat) || 0;
    
    const proteinOk = targetProtein === 0 || 
      Math.abs(totals.protein - targetProtein) / targetProtein <= (thresholds.protein / 100);
      
    const carbsOk = targetCarbs === 0 || 
      Math.abs(totals.carbs - targetCarbs) / targetCarbs <= (thresholds.carbs / 100);
      
    const fatOk = targetFat === 0 || 
      Math.abs(totals.fat - targetFat) / targetFat <= (thresholds.fat / 100);
    
    return withinCalorieThreshold && proteinOk && carbsOk && fatOk;
  };

  const roundToHalf = (value, servingUnit) => {
    // For countable items like eggs, round to nearest 0.5
    if (['egg', 'slice', 'piece', 'medium', 'large', 'small', 'fillet', 'patty', 'steak', 'thigh', 'head', 'handful', 'half', 'ball'].includes(servingUnit)) {
      return Math.round(value * 2) / 2; // Round to nearest 0.5
    }
    
    // For weight-based measurements, round to nearest whole number
    if (['g', 'oz', 'kg'].includes(servingUnit)) {
      return Math.round(value);
    }
    
    // For volume measurements, round to nearest 0.5
    return Math.round(value * 2) / 2;
  };

  // Updated optimization algorithm based on the provided Python script
  const optimizeMeal = (foods, targetMacros, thresholds) => {
    // Define target values
    const targetCals = parseFloat(targetMacros.calories);
    const targetProtein = parseFloat(targetMacros.protein) || 0;
    const targetCarbs = parseFloat(targetMacros.carbs) || 0;
    const targetFat = parseFloat(targetMacros.fat) || 0;

    // Define thresholds as decimal values
    const caloriesThreshold = thresholds.calories / 100;
    const proteinThreshold = thresholds.protein / 100;
    const carbsThreshold = thresholds.carbs / 100;
    const fatThreshold = thresholds.fat / 100;

    // Set maximum attempts and combinations
    const maxAttempts = 2000;
    const maxCombinations = 10;

    // Track valid combinations
    let validCombinations = [];
    let bestSolution = null;
    let bestScore = Infinity;

    // Use a flag to check if we should consider macros
    let macroFlag = thresholds.advancedMode;
    let attempts = 0;

    // Start with all selected foods
    let foodsToCalculate = foods.length;

    while (validCombinations.length < 1 && attempts < maxAttempts) {
      // Generate a potential meal combination
      const solution = foods.map(food => {
        // Get conversion factor for selected serving unit
        const conversionFactor = getConversionFactor(food, food.selectedServingUnit);

        // Generate random serving within desired range
        const minServing = food.desiredMin;
        const maxServing = food.desiredMax;
        const serving = Math.random() * (maxServing - minServing) + minServing;

        // Round to appropriate precision based on serving unit
        const roundedServing = roundToHalf(serving, food.selectedServingUnit);

        // Convert to base serving size for nutrition calculation
        const baseServingMultiplier = (roundedServing * conversionFactor) / food.servingSize;

        return {
          ...food,
          actualServingAmount: roundedServing,
          actualCalories: food.calories * baseServingMultiplier,
          actualProtein: food.protein * baseServingMultiplier,
          actualCarbs: food.carbs * baseServingMultiplier,
          actualFat: food.fat * baseServingMultiplier
        };
      });

      // Calculate totals
      const totals = solution.reduce((acc, item) => ({
        calories: acc.calories + item.actualCalories,
        protein: acc.protein + item.actualProtein,
        carbs: acc.carbs + item.actualCarbs,
        fat: acc.fat + item.actualFat
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

      // Check if the combination meets calorie threshold first
      const calorieError = Math.abs(totals.calories - targetCals) / targetCals;
      const withinCalorieThreshold = calorieError <= caloriesThreshold;

      if (withinCalorieThreshold) {
        // If we're not in advanced mode, or if we are in advanced mode and macros are within threshold
        if (!macroFlag || (
          (targetProtein === 0 || Math.abs(totals.protein - targetProtein) / targetProtein <= proteinThreshold) &&
          (targetCarbs === 0 || Math.abs(totals.carbs - targetCarbs) / targetCarbs <= carbsThreshold) &&
          (targetFat === 0 || Math.abs(totals.fat - targetFat) / targetFat <= fatThreshold)
        )) {
          // Calculate score based on how close we are to targets
          let score = calorieError;

          if (macroFlag) {
            if (targetProtein > 0) {
              const proteinError = Math.abs(totals.protein - targetProtein) / targetProtein;
              score += proteinError;
            }

            if (targetCarbs > 0) {
              const carbsError = Math.abs(totals.carbs - targetCarbs) / targetCarbs;
              score += carbsError;
            }

            if (targetFat > 0) {
              const fatError = Math.abs(totals.fat - targetFat) / targetFat;
              score += fatError;
            }
          }

          validCombinations.push({ foods: solution, totals, score });

          // Keep track of the best solution
          if (score < bestScore) {
            bestScore = score;
            bestSolution = { foods: solution, totals, score };
          }
        }
      }

      attempts++;

      // If we've tried many times and still don't have a valid combination, adjust strategy
      if (attempts >= maxAttempts / 2 && validCombinations.length === 0 && foodsToCalculate > foods.length / 2) {
        // Try with fewer foods
        foodsToCalculate--;
        attempts = 0;
      } else if (attempts >= maxAttempts && validCombinations.length === 0 && macroFlag) {
        // If we still don't have a valid combination, disable macro checking
        macroFlag = false;
        foodsToCalculate = foods.length;
        attempts = 0;
      }
    }

    // If we found valid combinations, return the best one
    if (validCombinations.length > 0) {
      // Sort by score to get the best combinations
      validCombinations.sort((a, b) => a.score - b.score);
      return validCombinations[0]; // Return the best combination
    }

    // If we didn't find any valid combinations, return a failure result
    return {
      foods: [],
      totals: { calories: 0, protein: 0, carbs: 0, fat: 0 },
      score: Infinity,
      failed: true
    };
  };

  const resetCalculator = () => {
    setSelectedFoods([]);
    setTargets({ calories: '', protein: '', carbs: '', fat: '' });
    setThresholds({ calories: 10, protein: 0, carbs: 0, fat: 0, advancedMode: false });
    setMealPlan(null);
    setCurrentStep(1);
    setHasShownPaymentPrompt(false);

    // If user has a profile, repopulate the targets
    if (userProfile && userProfile.calculatedTargets) {
      setTargets(userProfile.calculatedTargets);
      // Set thresholds based on advanced mode - enable advanced mode by default
      if (userProfile.calculatedTargets.protein && userProfile.calculatedTargets.carbs && userProfile.calculatedTargets.fat) {
        setThresholds({
          calories: 10,
          protein: 15,
          carbs: 15,
          fat: 15,
          advancedMode: true
        });
      }
    }
    
    // If user needs to pay, show the payment modal again
    if (checkNeedsPayment() && !hasShownPaymentPrompt) {
      setTimeout(() => {
        setShowPaymentModal(true);
        setHasShownPaymentPrompt(true);
      }, 500);
    }
  };

  const handleAddCustomFood = (newFood) => {
    setCustomFoods(prev => [...prev, newFood]);
    setShowCustomFoodForm(false);
  };

  const canCalculate = selectedFoods.length > 0 && targets.calories;

  const handleMealPlanUpdate = (updatedMealPlan) => {
    setMealPlan(updatedMealPlan);
  };

  const handleAddRecommendedFood = (foodToAdd) => {
    // Add the recommended food to selectedFoods
    setSelectedFoods(prevFoods => [...prevFoods, foodToAdd]);
    
    // Recalculate the meal plan
    setTimeout(() => {
      calculateMeal();
    }, 500);
  };

  const handleRemoveFood = (foodId) => {
    // Remove the food from meal plan
    if (mealPlan && mealPlan.foods) {
      const updatedFoods = mealPlan.foods.filter(food => food.id !== foodId);
      
      // Recalculate totals
      const newTotals = updatedFoods.reduce((acc, item) => ({
        calories: acc.calories + item.actualCalories,
        protein: acc.protein + item.actualProtein,
        carbs: acc.carbs + item.actualCarbs,
        fat: acc.fat + item.actualFat
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
      
      // Update meal plan
      setMealPlan({...mealPlan, foods: updatedFoods, totals: newTotals});
      
      // Also remove from selected foods
      setSelectedFoods(prevFoods => prevFoods.filter(food => food.id !== foodId));
    }
  };

  const handleProfileSave = (profileData) => {
    setUserProfile(profileData);
    setShowUserProfileForm(false);
    setSetupComplete(true);
    
    // Save to localStorage
    localStorage.setItem('user_profile', JSON.stringify(profileData));
    
    // Auto-populate targets based on profile
    if (profileData.calculatedTargets) {
      setTargets(profileData.calculatedTargets);
      
      // Set thresholds based on advanced mode - enable advanced mode by default
      if (profileData.calculatedTargets.protein && profileData.calculatedTargets.carbs && profileData.calculatedTargets.fat) {
        setThresholds({
          calories: 10,
          protein: 15,
          carbs: 15,
          fat: 15,
          advancedMode: true
        });
      }
    }
  };

  // Display remaining trials or premium status
  const renderUsageInfo = () => {
    if (loading) {
      return (
        <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-full flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-transparent" />
          <span>Loading...</span>
        </div>
      );
    }
    
    if (isPremium) {
      return (
        <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full flex items-center gap-2">
          <SafeIcon icon={FiStar} className="text-lg" />
          <span className="font-semibold">Premium User - Unlimited Access</span>
        </div>
      );
    }
    
    const remainingTrials = getRemainingFreeTrials();
    return (
      <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full flex items-center gap-2">
        <SafeIcon icon={FiZap} className="text-lg" />
        <span className="font-semibold">
          {remainingTrials} successful calculations remaining
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <SafeIcon icon={FiTarget} className="text-4xl text-indigo-600" />
            <h1 className="text-4xl font-bold text-gray-800">CreateMyMeal</h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Select foods with custom serving ranges, set macro targets with flexible thresholds,
            and get optimized meal plans tailored to your preferences.
          </p>
          
          {/* Usage Stats */}
          <div className="mt-4 flex items-center justify-center gap-4">
            {renderUsageInfo()}
          </div>
          
          {/* Action Buttons */}
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={() => setShowUserProfileForm(true)}
              className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
            >
              <SafeIcon icon={FiUser} className="text-lg" />
              <span>{userProfile ? 'Edit Profile' : 'Create Profile'}</span>
            </button>
            <button
              onClick={() => setShowGetStartedGuide(true)}
              className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
            >
              <SafeIcon icon={FiHelpCircle} className="text-lg" />
              <span>Get Started Guide</span>
            </button>
            <button
              onClick={() => setShowAnalyticsModal(true)}
              className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
            >
              <SafeIcon icon={FiBarChart} className="text-lg" />
              <span>Analytics</span>
            </button>
            <button
              onClick={() => setShowFeedbackModal(true)}
              className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
            >
              <SafeIcon icon={FiMessageCircle} className="text-lg" />
              <span>Feedback</span>
            </button>
          </div>
        </motion.div>

        {/* Progress Steps */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex justify-center mb-8"
        >
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    currentStep >= step ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step}
                </div>
                {step < 3 && (
                  <div
                    className={`w-16 h-1 mx-2 ${
                      currentStep > step ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column - Food Selection & Targets */}
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-700">Food Selection</h3>
              <button
                onClick={() => setShowCustomFoodForm(true)}
                className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiPlusCircle} className="text-lg" />
                <span>Add Custom Food</span>
              </button>
            </div>
            <FoodSelector
              selectedFoods={selectedFoods}
              setSelectedFoods={setSelectedFoods}
              onStepComplete={() => setCurrentStep(Math.max(currentStep, 2))}
              customFoods={customFoods}
            />
            <MacroTargets
              targets={targets}
              setTargets={setTargets}
              thresholds={thresholds}
              setThresholds={setThresholds}
              disabled={false} // Always allow editing after profile setup
            />
            
            {/* Calculate Button */}
            <motion.button
              whileHover={{ scale: canCalculate ? 1.02 : 1 }}
              whileTap={{ scale: canCalculate ? 0.98 : 1 }}
              onClick={calculateMeal}
              disabled={!canCalculate || isCalculating}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${
                canCalculate
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center justify-center gap-3">
                {isCalculating ? (
                  <>
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                    <span>Optimizing...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSliders} className="text-xl" />
                    <span>Optimize My Meal</span>
                  </>
                )}
              </div>
            </motion.button>

            {mealPlan && (
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={resetCalculator}
                className="w-full py-3 px-6 rounded-xl font-semibold bg-gray-200 hover:bg-gray-300 text-gray-700 transition-all duration-300"
              >
                Start New Calculation
              </motion.button>
            )}
          </div>

          {/* Right Column - Results */}
          <div>
            <AnimatePresence mode="wait">
              {mealPlan ? (
                <MealResults
                  key="results"
                  mealPlan={mealPlan}
                  targets={targets}
                  thresholds={thresholds}
                  onMealPlanUpdate={handleMealPlanUpdate}
                  selectedFoods={selectedFoods}
                  onAddFood={handleAddRecommendedFood}
                  onRemoveFood={handleRemoveFood}
                />
              ) : (
                <motion.div
                  key="placeholder"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="bg-white rounded-2xl shadow-lg p-8 text-center"
                >
                  <SafeIcon icon={FiCheckCircle} className="text-6xl text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    Ready to Calculate
                  </h3>
                  <p className="text-gray-500">
                    Select foods with desired amounts and set your targets to generate your optimized meal plan.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Custom Food Form Modal */}
      <AnimatePresence>
        {showCustomFoodForm && (
          <CustomFoodForm
            onClose={() => setShowCustomFoodForm(false)}
            onAddFood={handleAddCustomFood}
            nextId={foodDatabase.length + customFoods.length + 1}
          />
        )}
      </AnimatePresence>

      {/* Feedback Modal */}
      <AnimatePresence>
        {showFeedbackModal && (
          <FeedbackModal
            isOpen={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Get Started Guide */}
      <AnimatePresence>
        {showGetStartedGuide && (
          <GetStartedGuide
            isOpen={showGetStartedGuide}
            onClose={() => setShowGetStartedGuide(false)}
          />
        )}
      </AnimatePresence>

      {/* Analytics Modal */}
      <AnimatePresence>
        {showAnalyticsModal && (
          <AnalyticsModal
            isOpen={showAnalyticsModal}
            onClose={() => setShowAnalyticsModal(false)}
          />
        )}
      </AnimatePresence>

      {/* User Profile Form */}
      <AnimatePresence>
        {showUserProfileForm && (
          <UserProfileForm
            isOpen={showUserProfileForm}
            onClose={() => {
              if (setupComplete) {
                setShowUserProfileForm(false);
              }
            }}
            onSave={handleProfileSave}
            initialProfile={userProfile}
            allowSkip={setupComplete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MealCalculator;